import { useState, useEffect, useRef } from "react";
import { debounce } from "lodash";
import TextField from "@mui/material/TextField";
import {
    Box,
    Typography,
    Autocomplete,
    Button,
    Switch,
    CircularProgress,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import HorizontalFlex from "components/lib-ui/HorizontalFlex";
import { useForm, Controller } from "react-hook-form";
import Stack from "@mui/material/Stack";
import { GlobalAppDataStorageService } from "infra/storage";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import useFetchCompanies from "hooks/useFetchCompanies";
import useInsertCompany from "hooks/useInsertCompany";
import MeteredButton from "components/lib-ui/MeteredButton";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import GeneralUtilityService from "services/GeneralUtilityService";
import useSearchLocations from "hooks/api/useSearchLocations";
import EditIcon from "@mui/icons-material/Edit";

function mapHereLocationToBackend(location) {
    if (!location) return null;

    return {
        id: null,
        title: location.title ?? null,

        continent: null,

        country: location.locationInfo?.country
            ? {
                  id: null,
                  isoAlpha3Code:
                      location.locationInfo.country.isoAlpha3Code ?? null,
                  name: location.locationInfo.country.name ?? null,
              }
            : null,

        state: location.locationInfo?.state
            ? {
                  id: null,
                  name: location.locationInfo.state,
              }
            : null,

        county: location.locationInfo?.county
            ? {
                  id: null,
                  name: location.locationInfo.county,
              }
            : null,

        city: location.locationInfo?.city
            ? {
                  id: null,
                  name: location.locationInfo.city,
              }
            : null,

        district: location.locationInfo?.district
            ? {
                  id: null,
                  name: location.locationInfo.district,
              }
            : null,

        postalCode: location.locationInfo?.postalCode
            ? {
                  id: null,
                  code: location.locationInfo.postalCode,
              }
            : null,

        address: location.locationInfo?.address ?? null,

        latitude: location.coordinates?.latitude
            ? String(location.coordinates.latitude)
            : null,

        longitude: location.coordinates?.longitude
            ? String(location.coordinates.longitude)
            : null,

        locationSource: {
            id: 1,
            name: "here maps",
        },

        createdAt: null,
        updatedAt: null,
        lastVerifiedAt: null,
    };
}

const normalizeMonthValue = (value) => {
    if (typeof value !== "string") {
        return null;
    }

    const trimmedValue = value.trim();

    if (!trimmedValue) {
        return null;
    }

    return trimmedValue.slice(0, 7);
};

const isEndDateAfterStartDate = (startAt, endAt) => {
    const normalizedStartAt = normalizeMonthValue(startAt);
    const normalizedEndAt = normalizeMonthValue(endAt);

    if (!normalizedStartAt || !normalizedEndAt) {
        return true;
    }

    return normalizedEndAt > normalizedStartAt;
};

const mapExperienceToFormData = (experience) => {
    const mappedData = {
        id: experience.id,
        candidateId: experience.candidateId,
        company: {
            id: experience.companyId?.id ?? null,
            name: experience.companyId?.name ?? null,
            websiteUrl: experience.companyId?.websiteUrl ?? null,
        },
        jobTitle: experience.jobTitle,
        jobDescription: experience.jobDescription,
        startAt: experience.startAt + "-01",
        endAt: experience.endAt ? experience.endAt + "-01" : null,
        durationInYears: experience.durationInYears,
        updated_at: experience.updated_at,
        teamSize: experience.teamSize,
        industries: experience.industries,
        skills: experience.skills,
        responsibilities: experience.responsibilities,
        job_functions: experience.jobFunctions,
        location: mapHereLocationToBackend(experience?.location),
        occupations: experience?.occupations,
        jobChangeReason: experience.jobChangeReason,
        jobType: experience.jobType,
        workingModelId: experience.workingModelId,
    };

    if (!experience.location) {
        delete mappedData.location;
    }
    return mappedData;
};
export const ExperienceForm = ({
    onSubmit,
    experience,
    title,
    edit,
    setIsDialogOpen,
    setExperiences,
}) => {
    const experienceId = experience?.id;
    const experienceEndDate = experience?.endAt;
    const locationTitle = experience?.location?.title;
    const [isLocationUpdated, setIsLocationUpdated] = useState(false);

    const startAtToMonth = GeneralUtilityService.convertDateToMonthInput(
        experience?.startAt,
    );
    const endAtToMonth = GeneralUtilityService.convertDateToMonthInput(
        experience?.endAt,
    );

    const {
        control,
        register,
        watch,
        setValue,
        getValues,
        clearErrors,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: {
            startAt: edit ? startAtToMonth : null,
            endAt: edit ? endAtToMonth : null,
        },
    });
    const [showExtraOptions, setShowExtraOptions] = useState(false);
    const currentlyWorkingStatus = watch("currentlyWorking");
    const isCurrentlyWorking = experienceEndDate === null;

    useEffect(() => {
        if (currentlyWorkingStatus) {
            setValue("endAt", null);
            clearErrors("endAt");
        }
    }, [clearErrors, currentlyWorkingStatus, setValue]);
    const { insertCompany } = useInsertCompany();
    const { fetching, fetchCompaniesByName, companiesOptions } =
        useFetchCompanies();

    const {
        isSearchLocationsLoading,
        locations,
        fetchLocations,
        setLocations,
    } = useSearchLocations();

    const jobTypeOptions = GlobalAppDataStorageService.getJobTypes();
    const hardSkillsOptions = GlobalAppDataStorageService.getHardSkills();
    const companySizeOptions = GlobalAppDataStorageService.getCompanySizes();
    const companyTypesOptions = GlobalAppDataStorageService.getCompanyTypes();
    const industriesOptions = GlobalAppDataStorageService.getIndustries();
    const jobChangeReasonsOptions =
        GlobalAppDataStorageService.getJobChangeReasons();

    const occupationsOptions = GlobalAppDataStorageService.getOccupations();
    const jobFunctionsOptions = GlobalAppDataStorageService.getJobFunctions();
    const handleClick = (event) => {
        setShowExtraOptions(!showExtraOptions);
        event.target.focus();
    };

    const shouldFetchCompanies = (input) => {
        return Boolean(input?.trim()?.length >= 2);
    };

    const handleLocationInputChange = (event, value, reason) => {
        if (reason === "clear") {
            setLocations([]);
        }

        if (reason === "input") {
            fetchLocations(value);
        }
    };
    const changeExperiences = async (data) => {
        let nextData = data;

        if (showExtraOptions) {
            const companyId = await insertCompany(data);

            const companyIdObj = {
                id: companyId,
                name: data.name,
                websiteUrl: data.websiteUrl,
            };

            setValue("companyId", companyIdObj, {
                shouldDirty: true,
                shouldValidate: true,
            });

            nextData = { ...data, companyId: companyIdObj };
        }

        setExperiences((prev) => {
            if (edit) {
                return prev.map((exp) =>
                    exp.id === experienceId
                        ? { ...exp, ...mapExperienceToFormData(nextData) }
                        : exp,
                );
            }

            return [
                {
                    id: Math.random().toString(36).substring(2, 9),
                    ...mapExperienceToFormData(nextData),
                },
                ...prev,
            ];
        });

        setIsDialogOpen(false);
    };

    const debouncedFetchCompanyByName = useRef(
        debounce((value) => {
            fetchCompaniesByName(value);
        }, 500),
    ).current;

    const handleCompanyFetch = (event) => {
        if (shouldFetchCompanies(event.target.value))
            debouncedFetchCompanyByName(event.target.value);
    };

    const [renderNoOptionsComponent, setRenderNoOptionsComponent] =
        useState("Start typing");

    useEffect(() => {
        const companyNameValue = getValues("companyId")?.name;
        if (shouldFetchCompanies(companyNameValue))
            fetchCompaniesByName(companyNameValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDelete = () => {
        setExperiences((prevExperiences) =>
            prevExperiences.filter((exp) => exp.id !== experienceId),
        );
        setIsDialogOpen(false);
    };
    const shouldLocationBeDropdown = () => {
        return isLocationUpdated || !locationTitle;
    };
    return (
        <Box>
            <Typography variant="header2">{title} </Typography>
            <Stack mt={3} spacing={3}>
                {showExtraOptions ? (
                    <>
                        <Typography variant="header2">
                            Add a new company
                        </Typography>
                        <HorizontalFlex>
                            <TextField
                                variant="standard"
                                fullWidth
                                name="name"
                                {...register("name", {
                                    required: "Company name is required",
                                })}
                                defaultValue={""}
                                label="Company name"
                                error={!!errors.name}
                                helperText={errors.name?.message}
                            />
                            <Controller
                                name="companySizeId"
                                control={control}
                                defaultValue={null}
                                render={({ field }) => (
                                    <Autocomplete
                                        id="companySizeId"
                                        fullWidth
                                        autoComplete
                                        options={companySizeOptions || []}
                                        getOptionLabel={(option) =>
                                            option?.name || ""
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option.id === value.id
                                        }
                                        {...field}
                                        onChange={(event, newValue) => {
                                            field.onChange(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                variant="standard"
                                                {...params}
                                                label="Company size"
                                            />
                                        )}
                                    />
                                )}
                            />
                        </HorizontalFlex>
                        <HorizontalFlex>
                            <Controller
                                name="industries"
                                control={control}
                                defaultValue={[]}
                                render={({ field }) => (
                                    <Autocomplete
                                        id="industries"
                                        fullWidth
                                        multiple
                                        filterSelectedOptions
                                        autoComplete
                                        options={industriesOptions || []}
                                        getOptionLabel={(option) =>
                                            option.name || ""
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option.id === value.id
                                        }
                                        {...field}
                                        onChange={(event, newValue) => {
                                            field.onChange(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                variant="standard"
                                                {...params}
                                                label="Industry"
                                            />
                                        )}
                                    />
                                )}
                            />
                            <Controller
                                name="companyTypeId"
                                control={control}
                                defaultValue={null}
                                render={({ field }) => (
                                    <Autocomplete
                                        id="companyTypeId"
                                        fullWidth
                                        autoComplete
                                        options={companyTypesOptions || []}
                                        getOptionLabel={(option) =>
                                            option?.name || ""
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option.id === value.id
                                        }
                                        {...field}
                                        onChange={(event, newValue) => {
                                            field.onChange(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                variant="standard"
                                                {...params}
                                                label="Company type"
                                            />
                                        )}
                                    />
                                )}
                            />
                        </HorizontalFlex>
                        <HorizontalFlex>
                            <TextField
                                variant="standard"
                                fullWidth
                                name="websiteUrl"
                                {...register("websiteUrl", {})}
                                defaultValue={""}
                                label="Company url"
                            />
                            <TextField
                                variant="standard"
                                fullWidth
                                name="linkedinUrl"
                                {...register("linkedinUrl", {})}
                                defaultValue={""}
                                label="Company linkedin"
                            />
                        </HorizontalFlex>
                        <Button onClick={handleClick}>
                            Go back to existing companies
                        </Button>
                    </>
                ) : (
                    <></>
                )}
                {/* Company - Position */}
                <HorizontalFlex>
                    {!showExtraOptions ? (
                        <Controller
                            name="companyId"
                            control={control}
                            rules={{
                                required: "Company is required",
                            }}
                            defaultValue={edit ? experience?.company : null}
                            render={({ field }) => (
                                <Autocomplete
                                    id="companyId"
                                    fullWidth
                                    autoComplete
                                    options={companiesOptions}
                                    loading={fetching}
                                    loadingText={"Loading companies"}
                                    getOptionLabel={(option) =>
                                        option?.name || ""
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                    }
                                    {...field}
                                    onChange={(event, newValue) => {
                                        field.onChange(newValue);
                                    }}
                                    noOptionsText={renderNoOptionsComponent}
                                    renderInput={(params) => (
                                        <TextField
                                            variant="standard"
                                            onInput={handleCompanyFetch}
                                            {...params}
                                            error={!!errors.companyId}
                                            helperText={
                                                errors.companyId?.message
                                            }
                                            required
                                            label="Company"
                                            onChange={(event) => {
                                                const newInputValue =
                                                    event.target.value;
                                                if (
                                                    shouldFetchCompanies(
                                                        newInputValue,
                                                    ) &&
                                                    !fetching
                                                ) {
                                                    setRenderNoOptionsComponent(
                                                        <Button
                                                            onClick={
                                                                handleClick
                                                            }
                                                        >
                                                            No match? Click to
                                                            add company
                                                        </Button>,
                                                    );
                                                } else {
                                                    setRenderNoOptionsComponent(
                                                        "Start typing",
                                                    );
                                                }
                                            }}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {fetching && (
                                                            <CircularProgress
                                                                color="inherit"
                                                                size={20}
                                                            />
                                                        )}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            )}
                        />
                    ) : (
                        <></>
                    )}

                    <TextField
                        variant="standard"
                        fullWidth
                        defaultValue={edit ? experience?.jobTitle : ""}
                        {...register("jobTitle", {
                            required: "Position is required",
                        })}
                        required
                        label="Position"
                        error={!!errors.jobTitle}
                        helperText={errors.jobTitle?.message}
                    />
                </HorizontalFlex>
                {/* Start - End date */}
                <HorizontalFlex>
                    <TextField
                        variant="standard"
                        {...register("startAt", {
                            required: "Start date is required",
                        })}
                        fullWidth
                        required
                        InputLabelProps={{
                            shrink: true,
                        }}
                        name="startAt"
                        label="Start Date"
                        type="month"
                        error={!!errors.startAt}
                        helperText={
                            errors.startAt ? errors.startAt.message : ""
                        }
                    />
                    <TextField
                        variant="standard"
                        {...register("endAt", {
                            validate: (value) => {
                                if (currentlyWorkingStatus || !value) {
                                    return true;
                                }

                                return (
                                    isEndDateAfterStartDate(
                                        getValues("startAt"),
                                        value,
                                    ) ||
                                    "End date must be later than start date"
                                );
                            },
                        })}
                        name="endAt"
                        disabled={currentlyWorkingStatus}
                        fullWidth
                        InputLabelProps={{
                            shrink: true,
                        }}
                        label="End Date"
                        type="month"
                        error={!!errors.endAt}
                        helperText={errors.endAt?.message}
                    />
                </HorizontalFlex>
                <HorizontalFlex>
                    <Controller
                        name="currentlyWorking"
                        control={control}
                        defaultValue={isCurrentlyWorking} // Set the default value to false
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Switch
                                        {...field}
                                        checked={field.value}
                                        color="primary"
                                    />
                                }
                                label="Currently working there"
                            />
                        )}
                    />
                </HorizontalFlex>
                {!shouldLocationBeDropdown() && (
                    <HorizontalFlex sx={{ marginBottom: "1rem" }}>
                        <TextField
                            variant="standard"
                            InputProps={{
                                endAdornment: (
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        <EditIcon
                                            onClick={() =>
                                                setIsLocationUpdated(true)
                                            }
                                            sx={{ cursor: "pointer" }}
                                        />
                                    </Box>
                                ),
                            }}
                            sx={{
                                "& .MuiInputBase-input.Mui-disabled": {
                                    WebkitTextFillColor: "#000",
                                    opacity: 1,
                                },
                            }}
                            disabled
                            fullWidth
                            name="location"
                            defaultValue={locationTitle || undefined}
                            label="Location"
                        />
                    </HorizontalFlex>
                )}

                {shouldLocationBeDropdown() && (
                    <HorizontalFlex sx={{ marginBottom: "1rem" }}>
                        <Controller
                            name="location"
                            control={control}
                            defaultValue={""}
                            render={({ field }) => {
                                return (
                                    <Autocomplete
                                        filterOptions={(x) => x}
                                        id="location"
                                        fullWidth
                                        autoComplete
                                        loading={isSearchLocationsLoading}
                                        noOptionsText="No locations found"
                                        options={locations}
                                        {...field}
                                        getOptionLabel={(option) =>
                                            typeof option === "string"
                                                ? option
                                                : (option?.title ?? "")
                                        }
                                        isOptionEqualToValue={(
                                            option,
                                            value,
                                        ) => {
                                            if (typeof option === "string") {
                                                return option === value;
                                            }
                                            return option?.id === value?.id;
                                        }}
                                        onInputChange={
                                            handleLocationInputChange
                                        }
                                        onChange={(_, newValue) => {
                                            field.onChange(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="standard"
                                                label="Location"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        )}
                                    />
                                );
                            }}
                        />
                    </HorizontalFlex>
                )}
                {/* Experience skills - Responsibilities */}
                <HorizontalFlex>
                    <Controller
                        name="skills"
                        control={control}
                        fullWidth
                        defaultValue={edit ? experience?.skills : []}
                        render={({ field }) => (
                            <Autocomplete
                                id="skills"
                                fullWidth
                                multiple
                                filterSelectedOptions
                                autoComplete
                                limitTags={1}
                                options={hardSkillsOptions || []}
                                getOptionLabel={(option) => option.name || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                }
                                {...field}
                                onChange={(event, newValue) => {
                                    field.onChange(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        variant="standard"
                                        {...params}
                                        label="Skills"
                                    />
                                )}
                            />
                        )}
                    />
                </HorizontalFlex>
                {/* Job description */}
                <HorizontalFlex>
                    <TextField
                        variant="standard"
                        defaultValue={edit ? experience?.jobDescription : ""}
                        {...register("jobDescription")}
                        fullWidth
                        multiline
                        name="jobDescription"
                        label="Job description"
                    />
                </HorizontalFlex>
                <HorizontalFlex>
                    <Controller
                        name="occupations"
                        control={control}
                        defaultValue={edit ? experience.occupations : []}
                        render={({ field }) => (
                            <Autocomplete
                                id="occupations"
                                fullWidth
                                multiple
                                filterSelectedOptions
                                autoComplete
                                options={occupationsOptions || []}
                                getOptionLabel={(option) => option.name || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                }
                                {...field}
                                onChange={(event, newValue) => {
                                    field.onChange(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        variant="standard"
                                        {...params}
                                        label="Occupations"
                                    />
                                )}
                            />
                        )}
                    />
                </HorizontalFlex>
                <HorizontalFlex>
                    <Controller
                        name="jobFunctions"
                        control={control}
                        defaultValue={edit ? experience.job_functions : []}
                        render={({ field }) => (
                            <Autocomplete
                                id="jobFunctions"
                                fullWidth
                                multiple
                                filterSelectedOptions
                                autoComplete
                                options={jobFunctionsOptions || []}
                                getOptionLabel={(option) => option.name || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                }
                                {...field}
                                onChange={(event, newValue) => {
                                    field.onChange(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        variant="standard"
                                        {...params}
                                        label="Job Functions"
                                    />
                                )}
                            />
                        )}
                    />
                </HorizontalFlex>
                <HorizontalFlex>
                    <Controller
                        name="industries"
                        control={control}
                        defaultValue={edit ? experience.industries : []}
                        render={({ field }) => (
                            <Autocomplete
                                id="industries"
                                fullWidth
                                multiple
                                filterSelectedOptions
                                autoComplete
                                options={industriesOptions || []}
                                getOptionLabel={(option) => option.name || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                }
                                {...field}
                                onChange={(event, newValue) => {
                                    field.onChange(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        variant="standard"
                                        {...params}
                                        label="Experience Industries"
                                    />
                                )}
                            />
                        )}
                    />
                </HorizontalFlex>
                <HorizontalFlex>
                    <Controller
                        name="jobChangeReason"
                        control={control}
                        defaultValue={edit ? experience?.jobChangeReason : null}
                        render={({ field }) => (
                            <Autocomplete
                                id="jobChangeReason"
                                fullWidth
                                autoComplete
                                options={jobChangeReasonsOptions || []}
                                getOptionLabel={(option) => option.name}
                                value={field?.value || undefined}
                                isOptionEqualToValue={(option, value) =>
                                    option.name === value.name
                                }
                                {...field}
                                onChange={(event, newValue) => {
                                    field.onChange(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        variant="standard"
                                        {...params}
                                        label="Reason for job change"
                                    />
                                )}
                            />
                        )}
                    />
                    <Controller
                        name="jobType"
                        control={control}
                        defaultValue={edit ? experience?.jobType : null}
                        render={({ field }) => (
                            <Autocomplete
                                id="jobType"
                                fullWidth
                                autoComplete
                                options={jobTypeOptions || []}
                                getOptionLabel={(option) => option?.name || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                }
                                {...field}
                                onChange={(event, newValue) => {
                                    field.onChange(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        variant="standard"
                                        {...params}
                                        label="Job type"
                                    />
                                )}
                            />
                        )}
                    />
                </HorizontalFlex>
                <Stack direction="row" justifyContent="space-between">
                    {edit && (
                        <MeteredButton
                            onActionComplete={handleDelete}
                            buttonProps={{
                                color: "gray",
                                startIcon: <DeleteOutlinedIcon />,
                            }}
                        >
                            Delete
                        </MeteredButton>
                    )}

                    <LoadingButton
                        sx={{ marginLeft: "auto" }}
                        color="primary"
                        loadingPosition="start"
                        startIcon={<SaveIcon />}
                        type="button"
                        onClick={handleSubmit(changeExperiences)}
                    >
                        Save changes
                    </LoadingButton>
                </Stack>
            </Stack>
        </Box>
    );
};

export default ExperienceForm;
